import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Mail, Phone, MapPin, Globe, Instagram, ShieldCheck, ArrowLeft, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SERVICE_LABELS = {
  checkin: "Accueil voyageurs", key_handover: "Remise des clés", cleaning: "Ménage",
  linen: "Linge", photo: "Reportage photo", maintenance: "Maintenance",
  conciergerie_full: "Conciergerie clé en main", garden: "Jardin / piscine",
};
const LEGAL_LABELS = {
  "auto-entrepreneur": "Auto-entrepreneur", cesu: "CESU", "micro-entreprise": "Micro-entreprise",
  sasu: "SASU", sarl: "SARL", association: "Association", autre: "Autre",
};

export default function PartnerDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get(`/partners/${id}`).then(({ data }) => setP(data)).finally(() => setLoading(false));
  }, [id]);

  const sendMessage = async () => {
    if (!p?.user_id) {
      toast.error("Ce partenaire n'a pas de compte messagerie — utilisez email ou téléphone");
      return;
    }
    try {
      await api.post("/messages", { recipient_id: p.user_id, content: msg });
      toast.success("Message envoyé");
      setMsg("");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  if (loading) return <div className="pt-32 text-center text-neutral-500">Chargement…</div>;
  if (!p) return <div className="pt-32 text-center">Partenaire introuvable</div>;

  const resolveLogo = (l) => !l ? null : l.startsWith("http") ? l : `${process.env.REACT_APP_BACKEND_URL}/api/files/${l}`;
  const logoUrl = resolveLogo(p.logo);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/partenaires" className="inline-flex items-center gap-2 text-sm font-semibold mb-6 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Tous les partenaires
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {/* Logo + identity header */}
            <div className="flex items-start gap-5 mb-6">
              <div className="w-28 h-28 sm:w-32 sm:h-32 border border-black/10 bg-[#F5F2EC] flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl ? (
                  <img src={logoUrl} alt={`Logo ${p.company_name}`} className="w-full h-full object-cover" data-testid="partner-detail-logo" />
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 text-center px-2">Pas de logo</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
                    {p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"}
                  </div>
                  <span className="text-neutral-400">·</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-700 font-bold">{LEGAL_LABELS[p.legal_status]}</span>
                  {p.insurance && (
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#166534] text-white px-2 py-1 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Assuré RC pro
                    </span>
                  )}
                </div>
                <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">{p.company_name}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <MapPin className="w-4 h-4" />
              {[...(p.regions || []), ...(p.cities || [])].join(" · ") || "Bretagne"}
            </div>

            <div className="prose prose-sm max-w-none text-neutral-800 leading-relaxed whitespace-pre-line mt-6">
              {p.description}
            </div>

            {p.services?.length > 0 && (
              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3">Services</div>
                <div className="flex flex-wrap gap-2">
                  {p.services.map((s) => (
                    <span key={s} className="text-xs uppercase tracking-[0.1em] font-bold px-3 py-2 bg-[#F5F2EC] border border-black/10">
                      {SERVICE_LABELS[s] || s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {p.price_range && (
              <div className="mt-6 text-sm">
                <strong>Tarif indicatif :</strong> {p.price_range}
              </div>
            )}
          </div>

          {/* Contact panel */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 border border-black/15 bg-white p-6">
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3">Contact</div>
              {user ? (
                <div className="space-y-3">
                  {p.email && (
                    <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-sm hover:underline">
                      <Mail className="w-4 h-4" /> {p.email}
                    </a>
                  )}
                  {p.phone && (
                    <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-sm hover:underline">
                      <Phone className="w-4 h-4" /> {p.phone}
                    </a>
                  )}
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline">
                      <Globe className="w-4 h-4" /> Site web
                    </a>
                  )}
                  {p.instagram && (
                    <a href={p.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline">
                      <Instagram className="w-4 h-4" /> Instagram
                    </a>
                  )}
                  {p.user_id && (user.role === "owner" || user.role === "admin") && (
                    <div className="border-t border-black/10 pt-4 mt-4">
                      <textarea
                        value={msg}
                        onChange={(e) => setMsg(e.target.value)}
                        rows={3}
                        placeholder="Écrire un message…"
                        className="w-full border border-black/15 p-2 text-sm outline-none focus:border-[#0A0A0A]"
                      />
                      <button onClick={sendMessage} disabled={!msg.trim()} className="mt-2 w-full bg-[#0A0A0A] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#1B2A49] flex items-center justify-center gap-2 disabled:opacity-50">
                        <Send className="w-3.5 h-3.5" /> Envoyer un message
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-neutral-600 mb-3">Connectez-vous pour voir les coordonnées et contacter ce partenaire.</p>
                  <Link to="/login" className="block text-center bg-[#0A0A0A] text-white px-5 py-2.5 font-semibold hover:bg-[#1B2A49]">Se connecter</Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
